import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../login';
import { getCurrentLogo } from '../config/logoConfig';
import api from '../utils/api';

interface Citation {
  uuid: string;
  filename: string;
}

interface HeaderProps {
  selectedPage: string;
  onPageSelect: (page: string) => void;
}

const Header: React.FC<HeaderProps> = ({ selectedPage, onPageSelect }) => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCitationsOpen, setIsCitationsOpen] = useState(false);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const citationsRef = useRef<HTMLDivElement>(null);
  
  const currentLogo = getCurrentLogo();

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
  };

  const fetchCitations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/citation');
      setCitations(response.data.citations || []);
    } catch (err) {
      console.error('Failed to fetch citations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch citations');
    } finally {
      setLoading(false);
    }
  };

  const handleCitationClick = (citation: Citation) => {
    window.open(`/citation/${citation.uuid}/read`, '_blank');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (citationsRef.current && !citationsRef.current.contains(event.target as Node)) {
        setIsCitationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch citations when dropdown opens
  useEffect(() => {
    if (isCitationsOpen) {
      fetchCitations();
    }
  }, [isCitationsOpen]);

  return (
    <header className={`${currentLogo.headerColor} shadow-md border-b sticky top-0 z-50`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img src={currentLogo.src} alt={currentLogo.alt} className={`${currentLogo.size} w-auto`} />
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            {isAuthenticated && location.pathname === '/' && (
              <div className="flex space-x-4">
                {['Query engine', 'Knowledge Library'].map((page) => (
                  <button
                    key={page}
                    onClick={() => onPageSelect(page)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedPage === page
                        ? currentLogo.buttonColors.active
                        : currentLogo.buttonColors.inactive
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
            
            {isAuthenticated && user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentLogo.buttonColors.inactive}`}
                >
                  {/* User Icon */}
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">{user.full_name || user.username}</span>
                  {/* Chevron Down Icon */}
                  <svg className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      <div className="font-medium">{user.full_name || user.username}</div>
                      <div className="text-gray-500">{user.email}</div>
                      {user.is_admin && (
                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setIsCitationsOpen(!isCitationsOpen);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Citations
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-red-600 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Citations Table Dropdown */}
      {isCitationsOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-40" ref={citationsRef}>
          <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Citations</h2>
              <button
                onClick={() => setIsCitationsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-adalabs-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading citations...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-500 text-4xl mb-4">⚠️</div>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : citations.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">No citations found</p>
              </div>
            ) : (
              <div className="bg-white shadow-md rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        File Name
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {citations.map((citation) => (
                      <tr
                        key={citation.uuid}
                        onClick={() => handleCitationClick(citation)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {citation.filename}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 